// @flow
import React, { Fragment, useEffect, useCallback, useState } from 'react';
import { Container, Button, Icon } from 'semantic-ui-react';
import { errorLogger, infoLogger } from '../../helpers';
import ComviqContent from './ComviqContent';
import * as css from './Complete.module.css';
import Header from 'views/Header';
import Error from 'views/Error';
import Loading, { Progress, T2Spinner } from 'components';
import ReactGA from 'react-ga';
import api from 'api';
import useInterval from './useInterval';
import { useSelector } from 'react-redux';
import { isAppleDevice, isComviqCustomer } from '../../helpers/customerTypeHelpers';
import { getContentFor } from '../../helpers/contentHelpers';

type Props = {
  orderId: string,
  msisdn: string,
  eId: string,
  isTransfer?: boolean,
};

const Complete = ({ orderId, msisdn, eId, isTransfer = false }: Props) => {
  const [percent, setPercent] = useState(1);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [seconds, setSeconds] = useState(10);
  const [startCountDown, setStartCountDown] = useState(false);
  const [timerTextWithSeconds, setTimerTextWithSeconds] = useState('');
  const [timerText, setTimerText] = useState('');
  const [smsBody, setSmsBody] = useState({});
  let orderSteps = 8;
  let stepsTotal = 9; // We are adding an extra step when the order is finished, to get
  let orderStatusStep = 1;

  const customerType = useSelector((state) => state.app.customerType);
  const loading = useSelector((state) => state.app.isLoading);
  const error = useSelector((state) => state.app.error);

  const isComviq = isComviqCustomer(customerType);
  const allContent = getContentFor(customerType);
  const pageContent = allContent.page.complete;

  const fetchData = async (id) => {
    let orderStatus;
    try {
      // Get Order Status
      orderStatus = await api.rest(`v2/orderstatus/${customerType}/${orderId}`, {}, 'GET');

      if (orderStatus.stepCount && orderStatus.stepCount !== orderSteps) {
        orderSteps = orderStatus.stepCount;
        stepsTotal = orderSteps + 1;
      }

      if (orderStatus.stateCode && orderStatus.stateCode > orderStatusStep) {
        orderStatusStep = orderStatus.stateCode;
      }

      if (orderStatus.errorCode) {
        const customerBody = {
          Eid: eId,
          Msisdn: msisdn,
          HasUncompletedOrder: false,
          OrderId: '',
          CustomerType: customerType,
          Vendor: window.__VENDOR__,
        };
        await api.rest('v1/customerStates', customerBody, 'POST');

        errorLogger({
          name: 'client-orderstatus',
          log: 'OrderStatus returned with error, reset customer states',
          data: orderStatus,
        });
        setIsError(true);
        clearInterval(id);
      } else if (orderStatusStep === orderSteps) {
        clearInterval(id);
        setPercent((orderStatusStep / stepsTotal) * 100);
        try {
          const orderUrl = `v2/orders/${customerType}/${orderId}/${msisdn}`;
          const order = await api.rest(orderUrl, {}, 'GET');

          const paramsWithEid = {
            eid: eId,
            iccid: order.iccId,
            ...(isTransfer && { state: 'in-progress' }),
          };
          //init profile installation on watch
          if (isAppleDevice()) {
            // eslint-disable-next-line no-undef
            DataActivationController.dataPlanAccountUpdatedWithInfo(paramsWithEid);

            // TODO: Uncomment after a delay is implemented in the API
            // await api.rest('v1/shortmessageservices', smsBody, 'POST');
          } else {
            setTimeout(async () => {
              // TODO: Uncomment after a delay is implemented in the API
              // The SMS leads to customers leaving the WS flow
              // await api.rest('v1/shortmessageservices', smsBody, 'POST');

              await postData();
              infoLogger({ name: 'client-complete-subscription', log: 'Activate profile' });
              // eslint-disable-next-line no-undef
              AndroidSubscriptionManager.onPlanSelectionCompleted(1, 2, order.iccId, '', order.activationCode);
            }, 11000);
          }

          const infoLog = `Callback with params: ${JSON.stringify(paramsWithEid)}`;
          infoLogger({ name: 'client-profile-activation', log: infoLog });

          ReactGA.pageview('/completed');

          !isComviq && setStartCountDown(true);
          setPercent(100);
          infoLogger({
            name: 'client-profile-activation',
            log: 'Order completed',
          });
          await api.rest(`v1/sessions/updateNativeCallback`, {}, 'POST');
        } catch (error) {
          errorLogger({
            name: 'client-profile-activation',
            log: 'Data activation or short message services returned error in complete.js',
            data: error,
          });
          setErrorMsg(error.message);
          setIsError(true);
          clearInterval(id);
        }
      } else {
        setPercent((orderStatusStep / stepsTotal) * 100);
      }
    } catch (error) {
      errorLogger({
        name: 'client-fetch-data',
        log: 'Fetch data failed in complete.js',
        data: error.message,
      });
      setErrorMsg(error.message);
      setIsError(true);
      clearInterval(id);
    }
  };

  useInterval(async (id) => {
    fetchData(id);
  }, 1000);

  useEffect(() => {
    let timer;
    if (startCountDown && seconds > 0) {
      timer = setInterval(() => {
        setSeconds(seconds - 1);
      }, 1000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [startCountDown, seconds]);

  useEffect(() => {
    setTimerTextWithSeconds(timerText.replace('{{blob}}', seconds));
    if (seconds === 0) {
      if (isAppleDevice()) {
        completeSubscription();
      }
    }
  }, [seconds]);

  const postData = async () => {
    try {
      // Post Order'
      const customerBody = {
        Eid: eId,
        Msisdn: msisdn,
        HasUncompletedOrder: false,
        OrderId: orderId,
        CustomerType: customerType,
        Vendor: window.__VENDOR__,
      };
      await api.rest('v1/customerStates', customerBody, 'POST');
    } catch (error) {
      errorLogger({
        name: 'client-remove-customer-state',
        log: `Error setting hasUncompletedOrder to false, after completed subscription, with error message: ${error.message} `,
      });
      setErrorMsg(error.message);
    }
  };

  const completeSubscription = useCallback(() => {
    postData();
    /*eslint-disable no-undef*/
    try {
      infoLogger({ name: 'client-complete-subscription', log: 'Activate profile' });
      DataActivationController.doneSelected();
    } catch (error) {
      errorLogger({
        name: 'client-complete-subscription',
        log: 'Activation of profile failed with error, ' + error.message,
      });
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      setTimerText(pageContent.closeInformation);
      setSmsBody({
        Msisdn: msisdn,
        SmsTitle: pageContent.sms.title,
        Message: pageContent.sms.body,
      });
    }

    window.__PAGE__ = 'Complete';
    infoLogger({ name: 'client-page-view-complete', log: 'Complete page shown' });
  }, []);

  if (error || isError) return <Error message={errorMsg} />;
  if (loading || !customerType) return <Loading />;

  return (
    <Progress isComviq={isComviq} progress={percent}>
      <Header inverted={!isComviq && percent === 100} />
      <Container as="main" className={css.container}>
        {isComviq ? (
          <ComviqContent
            content={pageContent}
            percent={percent}
            timerTextWithSeconds={timerTextWithSeconds}
            completeSubscription={completeSubscription}
          />
        ) : (
          <>
            {percent < 100 && (
              <div className={css.contentContainer}>
                <h4 className={css.title}>{pageContent.loadingTitle}</h4>
                <p className={css.text}>{pageContent.loadingText}</p>
                <T2Spinner />
              </div>
            )}
            {percent === 100 && (
              <Fragment>
                <h4 className={css.title}>{pageContent.completeTitle}</h4>
                <p className={css.text}>{pageContent.completeText}</p>
                <p className={css.text}>{timerTextWithSeconds}</p>
                <div className={css.roundedCheckmark}>
                  <Icon name="check" />
                </div>
              </Fragment>
            )}
            <div className={css.progress}>{percent < 100 && <span>{Math.round(percent)}%</span>}</div>

            {percent === 100 && isAppleDevice() && (
              <Fragment>
                <Button color="white" className={`${css.close} link`} primary fluid onClick={completeSubscription}>
                  {pageContent.button}
                </Button>
              </Fragment>
            )}
          </>
        )}
      </Container>
    </Progress>
  );
};

export default React.memo(Complete);
